import { useState, useEffect, useCallback, useRef } from "react";
import { clsx } from "clsx";
import { useKeyPress } from "@/hooks";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DropdownOption } from "@/models/Dashboard";
import { SelectWithQuantities } from "@/crud/form/SelectWithQuantities";
import { BulkAction, BulkActionsButton } from "../BulkActionsButton";
import { Button } from "@/components/ui/button";
import { ListPlus, PlusIcon } from "lucide-react";
import { Input } from "@/components/ui/input";

type ColumnType =
  | "text"
  | "select"
  | "checkbox"
  | "select-with-quantities"
  | "read-only";

interface SheetColumn {
  name: string;
  type: ColumnType;
  options?: DropdownOption[]; // Only for select type
}

interface Props {
  columns: SheetColumn[];
  onSelectedRowsChange?: (selectedRows: number[]) => void;
  onTableDataChange?: (data: string[][]) => void;
  bulkActions?: Array<BulkAction>;
  initialData?: string[][]; // <-- add this
}

export const Sheet = ({
  columns,
  onSelectedRowsChange,
  onTableDataChange,
  bulkActions,
  initialData, // <-- add this
}: Props) => {
  // Data storage - array of arrays representing the table data
  const [tableData, setTableData] = useState<string[][]>(() => {
    if (initialData && Array.isArray(initialData) && initialData.length > 0) {
      // Pad rows to columns.length if needed
      return initialData.map((row) =>
        Array.from({ length: columns.length }, (_, i) => row[i] ?? "")
      );
    }
    // Initialize with empty strings for all cells (start with 4 rows)
    return Array(4)
      .fill(null)
      .map(() => Array(columns.length).fill(""));
  });

  // Only once, after initialData is set, we want to skip the onTableDataChange call
  useEffect(() => {
    if (initialData && onTableDataChange) {
      onTableDataChange(initialData);
    }
  }, []);

  // If initialData changes, update tableData
  // useEffect(() => {
  //   if (initialData && Array.isArray(initialData) && initialData.length > 0) {
  //     setTableData(
  //       initialData.map((row) =>
  //         Array.from({ length: columns.length }, (_, i) => row[i] ?? "")
  //       )
  //     );
  //   }
  //   // eslint-disable-next-line
  // }, [initialData, columns.length]);

  // Which cell is active
  const [activeCell, setActiveCell] = useState<Cell | null>(null);

  // Ref for spreadsheet container to get its width
  const sheetRef = useRef<HTMLDivElement>(null);

  // The current column widths (in px)
  const [columnWidths, setColumnWidths] = useState<number[]>(() => {
    // Default: fill 100% of parent, divided equally
    return Array(columns.length).fill(0);
  });

  // --- Checkbox column width calculation ---
  // Helper to measure text width (for checkbox headers)
  function measureTextWidth(text: string, font = "14px Inter, sans-serif") {
    if (typeof window === "undefined") return 60;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return 60;
    ctx.font = font;
    return Math.ceil(ctx.measureText(text).width) + 24; // 24px for padding/checkbox
  }

  // Calculate checkbox column widths (header text + padding)
  const [checkboxColWidths, setCheckboxColWidths] = useState<number[]>([]);

  useEffect(() => {
    // Only run on client
    const widths = columns.map((col) =>
      col.type === "checkbox" ? measureTextWidth(col.name) : 0
    );
    setCheckboxColWidths(widths);
    // eslint-disable-next-line
  }, [columns]);

  // Calculate total width reserved for checkbox columns
  const totalCheckboxWidth = checkboxColWidths.reduce((a, b) => a + b, 0);

  // Calculate percent widths for non-checkbox columns so table fills 100%
  const nonCheckboxCount = columns.filter((c) => c.type !== "checkbox").length;
  const nonCheckboxPercents = columns.map((col, idx) =>
    col.type === "checkbox" ? 0 : 100 / (nonCheckboxCount || 1)
  );

  // Use state for columnPercents for resizable columns
  const [columnPercents, setColumnPercents] =
    useState<number[]>(nonCheckboxPercents);

  // Reset columnPercents if columns change
  useEffect(() => {
    setColumnPercents(nonCheckboxPercents);
    // eslint-disable-next-line
  }, [columns.length]);

  // Set initial column widths to fill parent on mount or when columns change
  useEffect(() => {
    function recalcWidths() {
      if (sheetRef.current) {
        const parentWidth = sheetRef.current.offsetWidth;
        setColumnWidths((prev) => {
          // If prev is all zeros or wrong length, just split evenly
          if (prev.length !== columns.length || prev.every((w) => w === 0)) {
            const colWidth = Math.floor(parentWidth / columns.length);
            return Array(columns.length).fill(colWidth);
          }
          // Otherwise, keep proportions
          const totalPrev = prev.reduce((a, b) => a + b, 0) || 1;
          return prev.map((w) =>
            Math.max(50, Math.round((w / totalPrev) * parentWidth))
          );
        });
      }
    }

    recalcWidths();

    window.addEventListener("resize", recalcWidths);
    return () => window.removeEventListener("resize", recalcWidths);
  }, [columns.length]);

  // --- Resizing logic for non-checkbox columns only ---
  const [isResizing, setIsResizing] = useState<number | null>(null);

  const handleMouseDown = (columnIndex: number) => (e: React.MouseEvent) => {
    // Only allow resizing if this and next column are not checkbox columns
    if (
      columns[columnIndex].type === "checkbox" ||
      columns[columnIndex + 1]?.type === "checkbox"
    ) {
      return;
    }
    e.preventDefault();
    setIsResizing(columnIndex);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizing !== null && sheetRef.current) {
        // Only allow resizing if this and next column are not checkbox columns
        if (
          columns[isResizing].type === "checkbox" ||
          columns[isResizing + 1]?.type === "checkbox"
        ) {
          return;
        }
        e.preventDefault();
        const minPercent = 5;
        const parentWidth = sheetRef.current.offsetWidth - totalCheckboxWidth;
        const deltaX = e.movementX;
        const percents = [...columnPercents];

        if (isResizing < columns.length - 1) {
          const colWidth = (percents[isResizing] / 100) * parentWidth;
          const nextColWidth = (percents[isResizing + 1] / 100) * parentWidth;

          let newColWidth = colWidth + deltaX;
          let newNextColWidth = nextColWidth - deltaX;

          const minPx = (minPercent / 100) * parentWidth;
          newColWidth = Math.max(minPx, newColWidth);
          newNextColWidth = Math.max(minPx, newNextColWidth);

          percents[isResizing] = (newColWidth / parentWidth) * 100;
          percents[isResizing + 1] = (newNextColWidth / parentWidth) * 100;

          // Normalize so total is exactly the sum of the two columns before
          const sum = percents[isResizing] + percents[isResizing + 1];
          const origSum =
            columnPercents[isResizing] + columnPercents[isResizing + 1];
          if (Math.abs(sum - origSum) > 0.01) {
            const diff = origSum - sum;
            percents[isResizing + 1] += diff;
          }
          setColumnPercents(percents);
        }
      }
    };

    if (isResizing !== null) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", () => setIsResizing(null), {
        once: true,
      });
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
      };
    }
  }, [isResizing, columnPercents, columns, totalCheckboxWidth]);

  // Reset columnPercents if columns change
  useEffect(() => {
    setColumnPercents(Array(columns.length).fill(100 / columns.length));
  }, [columns.length]);

  // Handle input changes and update table data
  const handleCellChange = (row: number, column: number, value: any) => {
    setTableData((prevData) => {
      const newData = [...prevData];
      newData[row] = [...newData[row]];
      // For checkbox columns, store boolean true/false
      if (columns[column].type === "checkbox") {
        newData[row][column] = !!value;
      } else {
        newData[row][column] = value;
      }
      return newData;
    });
  };

  // Tabbing
  const handleTabPress = (event?: KeyboardEvent) => {
    // Only handle tab if there's an active cell
    if (!activeCell) return;

    event?.preventDefault(); // Prevent default tab behavior only when we have an active cell

    const nextColumn = activeCell.column + 1;
    const nextRow = activeCell.row + 1;

    // If we can move to the next column in the same row
    if (nextColumn < columns.length) {
      setActiveCell({ column: nextColumn, row: activeCell.row });
    }
    // If we're at the end of the row, wrap to the first column of the next row
    else if (nextRow < tableData.length) {
      setActiveCell({ column: 0, row: nextRow });
    }
    // If we're at the last cell, wrap to the first cell
    else {
      setActiveCell({ column: 0, row: 0 });
    }
    // Clear selection when moving with Tab
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  // Make tab key presses handleable
  useKeyPress("Tab", handleTabPress);

  // Left right, up and down arrow keys for navigating the sheet
  const handleArrowPress = (event?: KeyboardEvent) => {
    if (!activeCell || !event) return;

    // Prevent default arrow key behavior in input fields
    event.preventDefault();

    let newColumn = activeCell.column;
    let newRow = activeCell.row;

    // Calculate new active cell based on arrow key pressed
    switch (event.key) {
      case "ArrowRight":
        newColumn = Math.min(activeCell.column + 1, columns.length - 1);
        break;
      case "ArrowLeft":
        newColumn = Math.max(activeCell.column - 1, 0);
        break;
      case "ArrowDown":
        // Only go down if the ShadCN Dropdown isn't open
        if (!isShadcnSelectOpen(activeCell.row, activeCell.column)) {
          newRow = Math.min(activeCell.row + 1, tableData.length - 1);
        }
        break;
      case "ArrowUp":
        newRow = Math.max(activeCell.row - 1, 0);
        break;
      default:
        return; // Exit if the key is not an arrow key
    }

    setActiveCell({ column: newColumn, row: newRow });
    // Clear selection when moving with arrows
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  // Make arrow key presses handleable
  useKeyPress("ArrowRight", handleArrowPress);
  useKeyPress("ArrowLeft", handleArrowPress);
  useKeyPress("ArrowDown", handleArrowPress);
  useKeyPress("ArrowUp", handleArrowPress);

  // Helper: get visible columns (not read-only)
  const visibleColumns = columns.filter((col) => col.type !== "read-only");

  // Paste functionality
  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      if (!activeCell) return;
      event.preventDefault();

      try {
        const clipboardData = await navigator.clipboard.readText();
        if (!clipboardData.trim()) return;

        // Split into rows and cells
        const rows = clipboardData
          .split("\n")
          .filter((row) => row.trim() !== "");
        const parsedData: string[][] = [];

        for (const row of rows) {
          let cells: string[];
          if (row.includes("\t")) {
            cells = row.split("\t");
          } else {
            cells = row
              .split(",")
              .map((cell) => cell.trim().replace(/^"|"$/g, ""));
          }
          parsedData.push(cells);
        }

        // If it's a single value (not tab/comma separated), just paste into the active cell
        if (
          parsedData.length === 1 &&
          parsedData[0].length === 1 &&
          parsedData[0][0] !== ""
        ) {
          setTableData((prevData) => {
            const newData = [...prevData];
            newData[activeCell.row] = [...newData[activeCell.row]];
            newData[activeCell.row][activeCell.column] = parsedData[0][0];
            return newData;
          });
          return;
        }

        // Otherwise, paste as a block (spreadsheet style)
        const startRow = activeCell.row;
        const endRow = startRow + parsedData.length - 1;

        setTableData((prevData) => {
          const newData = [...prevData];

          while (newData.length <= endRow) {
            newData.push(Array(columns.length).fill(""));
          }

          for (let i = 0; i < parsedData.length; i++) {
            const targetRow = startRow + i;
            if (targetRow >= newData.length) break;
            newData[targetRow] = [...newData[targetRow]];
            let visibleColIdx = 0;
            for (let j = 0; j < columns.length; j++) {
              if (columns[j].type === "read-only") continue;
              if (visibleColIdx < parsedData[i].length) {
                const col = columns[j];
                let val = parsedData[i][visibleColIdx] || "";
                // Marshall pasted value based on column type
                if (col.type === "checkbox") {
                  val = parseCheckboxValue(val);
                } else if (
                  col.type === "select-with-quantities" &&
                  col.options
                ) {
                  val = parseSelectWithQuantitiesValue(val, col.options);
                }
                newData[targetRow][j] = val;
                visibleColIdx++;
              }
            }
          }

          return newData;
        });
      } catch (error) {
        console.error("Failed to read clipboard data:", error);
      }
    },
    [activeCell, columns, setTableData, visibleColumns.length]
  );

  // Add paste event listener
  useEffect(() => {
    // when we paste into the sheet call handlePaste
    const handlePasteEvent = (e: ClipboardEvent) => handlePaste(e);
    document.addEventListener("paste", handlePasteEvent);

    return () => {
      document.removeEventListener("paste", handlePasteEvent);
    };
  }, [activeCell, handlePaste]);

  // make it so that anytime the active cell changes, the input field is focused
  useEffect(() => {
    if (activeCell) {
      const inputElement = document.querySelector(
        `input[data-row="${activeCell.row}"][data-column="${activeCell.column}"]`
      ) as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, [activeCell]);

  // For dragging
  // where the dragging began
  const [selectionStart, setSelectionStart] = useState<Cell | null>(null);
  // the current hover point while hovering
  const [selectionEnd, setSelectionEnd] = useState<Cell | null>(null);
  // whether the user is currently dragging
  const [isDragging, setIsDragging] = useState(false);

  // theres no global mouseup event listener, so we add one to the window
  // to stop dragging when the user releases the mouse button
  // this is to ensure that if the user drags outside of the app, we still stop dragging
  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  // this function checks if a cell is in the current selection
  // it checks if the cell is between the start and end of the selection both horizontally and vertically
  // if it's true and it's in the selection, we use it for highlighting
  const isCellInSelection = (row: number, column: number): boolean => {
    if (!selectionStart || !selectionEnd) return false;

    const startRow = Math.min(selectionStart.row, selectionEnd.row);
    const endRow = Math.max(selectionStart.row, selectionEnd.row);
    const startCol = Math.min(selectionStart.column, selectionEnd.column);
    const endCol = Math.max(selectionStart.column, selectionEnd.column);

    return (
      row >= startRow && row <= endRow && column >= startCol && column <= endCol
    );
  };

  // Tracks what cells are selected
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  // generate a key that uniquely identifies a cell based on its row and column
  const getCellKey = (row: number, col: number) => `row:${row},col:${col}`;

  // Update selected cells based on the current selection
  useEffect(() => {
    // If there's no selection start or end, clear selected cells
    if (!selectionStart || !selectionEnd) {
      setSelectedCells(new Set());
      return;
    }

    // Generate new selected cells based on the current selection
    const startRow = Math.min(selectionStart.row, selectionEnd.row);
    const endRow = Math.max(selectionStart.row, selectionEnd.row);
    const startCol = Math.min(selectionStart.column, selectionEnd.column);
    const endCol = Math.max(selectionStart.column, selectionEnd.column);

    // Use a Set for selected cells
    const newSelected = new Set<string>();
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        newSelected.add(getCellKey(row, col));
      }
    }
    setSelectedCells(newSelected);
  }, [selectionStart, selectionEnd]);

  // Copy selected cells to clipboard as TSV on Ctrl+C/Cmd+C
  useEffect(() => {
    const handleCopy = (e: KeyboardEvent) => {
      // Only copy if there is a selection and not editing a cell
      if (selectedCells.size > 0) {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
          e.preventDefault();

          // Find selection bounds
          if (!selectionStart || !selectionEnd) return;
          const startRow = Math.min(selectionStart.row, selectionEnd.row);
          const endRow = Math.max(selectionStart.row, selectionEnd.row);
          const startCol = Math.min(selectionStart.column, selectionEnd.column);
          const endCol = Math.max(selectionStart.column, selectionEnd.column);

          // Collect selected data
          const lines: string[] = [];
          for (let row = startRow; row <= endRow; row++) {
            const line: string[] = [];
            for (let col = startCol; col <= endCol; col++) {
              line.push(tableData[row]?.[col] ?? "");
            }
            lines.push(line.join("\t"));
          }
          const tsv = lines.join("\n");

          // Write to clipboard
          if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(tsv);
          } else {
            // fallback for insecure context
            const textarea = document.createElement("textarea");
            textarea.value = tsv;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
          }
        }
      }
    };
    window.addEventListener("keydown", handleCopy);
    return () => window.removeEventListener("keydown", handleCopy);
  }, [selectedCells, selectionStart, selectionEnd, tableData]);

  // Add this style block at the top level of your component file (or in your CSS file)
  const noselectStyle = `
    .noselect, .noselect * {
      user-select: none !important;
    }
  `;

  const getSelectionClasses = (row: number, col: number) => {
    const classes = ["border-blue-500/30", "bg-blue-500/5"];
    if (col === 0) {
      classes.push("border-blue-500/30", "border-l");
    } else {
      classes.push("border-l-white/10", "border-l");
    }
    if (row === 0) {
      classes.push("border-t");
    } else {
      classes.push("border-t-white/10", "border-t");
    }
    if (row == selectionEnd.row) {
      if (col === 0) {
        classes.push("rounded-bl-sm");
      }
      classes.push("border-b");
    }
    if (col == selectionEnd.column) {
      if (row === 0) {
        classes.push("rounded-tr-sm");
      }
      classes.push("border-r");
    }

    if (row === selectionEnd.row && col === selectionEnd.column) {
      classes.push("rounded-br-sm");
    }

    return classes.join(" ");
  };

  const [isFillDragging, setIsFillDragging] = useState(false);
  const [fillTarget, setFillTarget] = useState<Cell | null>(null);

  // Helper: get selection bounds (returns {startRow, endRow, startCol, endCol})
  const getSelectionBounds = () => {
    if (!selectionStart || !selectionEnd) return null;
    return {
      startRow: Math.min(selectionStart.row, selectionEnd.row),
      endRow: Math.max(selectionStart.row, selectionEnd.row),
      startCol: Math.min(selectionStart.column, selectionEnd.column),
      endCol: Math.max(selectionStart.column, selectionEnd.column),
    };
  };

  // Helper: check if a cell is the bottom-right of the selection
  const isBottomRightOfSelection = (row: number, col: number) => {
    const bounds = getSelectionBounds();
    if (!bounds) return false;
    return row === bounds.endRow && col === bounds.endCol;
  };

  // Mouse event handlers for fill dot
  const handleFillMouseDown =
    (row: number, col: number) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsFillDragging(true);
      setFillTarget({ row, column: col });
    };

  // Track fill drag over cells
  useEffect(() => {
    if (!isFillDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const cellDiv = (el as HTMLElement).closest("[data-row][data-column]");
      if (!cellDiv) return;
      const row = Number((cellDiv as HTMLElement).getAttribute("data-row"));
      const col = Number((cellDiv as HTMLElement).getAttribute("data-column"));
      setFillTarget({ row, column: col });
    };

    const handleMouseUp = () => {
      setIsFillDragging(false);
      const bounds = getSelectionBounds();
      if (!bounds || !fillTarget) return;
      // Only fill if drag went outside the original selection
      if (
        fillTarget.row !== bounds.endRow ||
        fillTarget.column !== bounds.endCol
      ) {
        // Compute fill region (rectangle from selection to fillTarget)
        const fillRect = {
          startRow: Math.min(bounds.startRow, fillTarget.row),
          endRow: Math.max(bounds.endRow, fillTarget.row),
          startCol: Math.min(bounds.startCol, fillTarget.column),
          endCol: Math.max(bounds.endCol, fillTarget.column),
        };

        setTableData((prevData) => {
          const newData = [...prevData.map((row) => [...row])];
          // Get the selection values
          const selVals: string[][] = [];
          for (let r = bounds.startRow; r <= bounds.endRow; r++) {
            selVals.push(newData[r].slice(bounds.startCol, bounds.endCol + 1));
          }
          const selRows = selVals.length;
          const selCols = selVals[0]?.length || 1;

          // Fill the region (excluding the original selection)
          for (let r = fillRect.startRow; r <= fillRect.endRow; r++) {
            for (let c = fillRect.startCol; c <= fillRect.endCol; c++) {
              // Only fill if outside the original selection
              if (
                r < bounds.startRow ||
                r > bounds.endRow ||
                c < bounds.startCol ||
                c > bounds.endCol
              ) {
                // Repeat selection pattern
                const val =
                  selVals[(r - bounds.startRow + selRows) % selRows][
                    (c - bounds.startCol + selCols) % selCols
                  ];
                if (newData[r] && typeof newData[r][c] !== "undefined") {
                  newData[r][c] = val;
                }
              }
            }
          }
          return newData;
        });
      }
      setFillTarget(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp, { once: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isFillDragging, fillTarget, selectionStart, selectionEnd, setTableData]);

  // Delete selected rows on Backspace
  useEffect(() => {
    const handleDeleteRows = (e: KeyboardEvent) => {
      if (
        document.activeElement &&
        (document.activeElement as HTMLElement).tagName === "INPUT"
      ) {
        // Don't delete if editing a cell
        return;
      }
      if (e.key === "Backspace" && selectionStart && selectionEnd) {
        e.preventDefault();
        const startRow = Math.min(selectionStart.row, selectionEnd.row);
        const endRow = Math.max(selectionStart.row, selectionEnd.row);

        setTableData((prev) => {
          // Remove rows from startRow to endRow (inclusive)
          const newData = prev.filter(
            (_, idx) => idx < startRow || idx > endRow
          );
          // Always keep at least one empty row
          if (newData.length === 0) {
            return [Array(columns.length).fill("")];
          }
          return newData;
        });

        // Reset selection and active cell
        setSelectionStart(null);
        setSelectionEnd(null);
        setActiveCell(null);
      }
    };
    window.addEventListener("keydown", handleDeleteRows);
    return () => window.removeEventListener("keydown", handleDeleteRows);
  }, [selectionStart, selectionEnd, columns.length]);

  // Unselect active cell and selection when clicking outside the sheet,
  // but ignore clicks inside any open shadcn select dropdown
  const sheetContainerRef = useRef<HTMLDivElement>(null);

  // For handling clicks outside of the sheet
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Check if click is inside the sheet
      if (
        sheetContainerRef.current &&
        sheetContainerRef.current.contains(e.target as Node)
      ) {
        return;
      }
      // Check if click is inside a shadcn select dropdown (by class)
      const target = e.target as HTMLElement;
      if (
        target.closest(".radix-select-content") || // shadcn select content
        target.closest("[data-radix-popper-content-wrapper]") // fallback for popper wrapper
      ) {
        return;
      }
      setActiveCell(null);
      setSelectionStart(null);
      setSelectionEnd(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Add this state to track which select cell should be open
  const [openSelectCell, setOpenSelectCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  // When activeCell changes, if it's a select cell, open the dropdown
  useEffect(() => {
    if (activeCell && columns[activeCell.column]?.type === "select") {
      setOpenSelectCell({ row: activeCell.row, col: activeCell.column });
    } else {
      setOpenSelectCell(null);
    }
  }, [activeCell, columns]);

  // To check if a shadcn Select is open for a given cell, compare openSelectCell to the cell's coordinates:
  const isShadcnSelectOpen = (row: number, col: number) =>
    openSelectCell?.row === row && openSelectCell?.col === col;

  // Add this state to track which select-with-quantities cell should be open
  const [openQuantitiesCell, setOpenQuantitiesCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  // When activeCell changes, if it's a select-with-quantities cell, open the dropdown
  useEffect(() => {
    if (
      activeCell &&
      columns[activeCell.column]?.type === "select-with-quantities"
    ) {
      setOpenQuantitiesCell({ row: activeCell.row, col: activeCell.column });
    } else {
      setOpenQuantitiesCell(null);
    }
  }, [activeCell, columns]);

  // Helper to check if a select-with-quantities dropdown should be open
  const isQuantitiesDropdownOpen = (row: number, col: number) =>
    openQuantitiesCell?.row === row && openQuantitiesCell?.col === col;

  // Add this state to track selected rows
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // keep latest callback without re-triggering the effect
  const onSelectedRowsChangeRef = useRef(onSelectedRowsChange);
  useEffect(() => {
    onSelectedRowsChangeRef.current = onSelectedRowsChange;
  }, [onSelectedRowsChange]);

  // guard + dedupe
  const didInitSelectedRef = useRef(false);
  const prevSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    // stable order for comparison + consumer convenience
    const arr = Array.from(selectedRows).sort((a, b) => a - b);
    const serialized = JSON.stringify(arr);

    // skip first run (initial render)
    if (!didInitSelectedRef.current) {
      didInitSelectedRef.current = true;
      prevSelectedRef.current = serialized;
      return;
    }

    // only notify if changed
    if (prevSelectedRef.current !== serialized) {
      prevSelectedRef.current = serialized;
      onSelectedRowsChangeRef.current?.(arr);
    }
  }, [selectedRows]);

  // Handler for toggling a single row's checkbox
  const handleRowCheckbox = (rowIndex: number, checked: boolean) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(rowIndex);
      } else {
        next.delete(rowIndex);
      }
      return next;
    });
  };

  // Handler for toggling all checkboxes (header)
  const handleHeaderCheckbox = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(tableData.map((_, idx) => idx)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Toggle checkbox cell value with Enter key if active cell is a checkbox
  useKeyPress("Enter", () => {
    if (activeCell && columns[activeCell.column]?.type === "checkbox") {
      setTableData((prevData) => {
        const newData = [...prevData];
        newData[activeCell.row] = [...newData[activeCell.row]];
        const current = !!newData[activeCell.row][activeCell.column];
        newData[activeCell.row][activeCell.column] = !current;
        return newData;
      });
    }
  });

  // on table data change, guarded version to prevent initial re-render call
  const onTableDataChangeRef = useRef(onTableDataChange);
  useEffect(() => {
    onTableDataChangeRef.current = onTableDataChange;
  }, [onTableDataChange]);

  const didInitRef = useRef(false);
  const prevNormalizedRef = useRef<string | null>(null);

  useEffect(() => {
    // build filtered + normalized exactly as you do now
    const filtered = tableData.filter((row) =>
      row.some((cell, idx) =>
        columns[idx].type === "checkbox"
          ? cell === true
          : columns[idx].type === "select-with-quantities"
          ? cell && typeof cell === "object" && Object.keys(cell).length > 0
          : cell !== "" && cell != null
      )
    );
    const normalized = filtered.map((row) =>
      row.map((cell, idx) => (columns[idx].type === "checkbox" ? !!cell : cell))
    );

    // stringify once for stable deep-equality check
    const serialized = JSON.stringify(normalized);

    // skip the very first run (mount)
    if (!didInitRef.current) {
      didInitRef.current = true;
      prevNormalizedRef.current = serialized;
      return;
    }

    // only notify if the outgoing payload actually changed
    if (prevNormalizedRef.current !== serialized) {
      prevNormalizedRef.current = serialized;
      onTableDataChangeRef.current?.(normalized);
    }
    // deps: only data that truly changes the payload shape/content
  }, [tableData, columns]); // (not the callback!)

  // Add row controls
  const [addRowsCount, setAddRowsCount] = useState(1);

  // Handler to add a single row
  const handleAddRow = () => {
    setTableData((prev) => [...prev, Array(columns.length).fill("")]);
  };

  // Handler to add multiple rows
  const handleAddRows = () => {
    const count = Math.max(1, Math.min(100, Number(addRowsCount) || 1));
    setTableData((prev) => [
      ...prev,
      ...Array(count)
        .fill(null)
        .map(() => Array(columns.length).fill("")),
    ]);
  };

  return (
    <>
      {/* <div>
        Sheet tips. Tab to move to the next item. Can navigate with arrows.
        Enter to toggle checkboxes. Enter to enter dropdowns
      </div> */}
      <style>{noselectStyle}</style>
      <div className="select-none w-full">
        <div
          ref={(el) => {
            sheetRef.current = el;
            sheetContainerRef.current = el;
          }}
          style={{ minWidth: 0 }}
          className="px-2"
        >
          {/* Header row with column names */}
          <div className="flex w-full">
            {columns.map((column, columnIndex) =>
              column.type === "read-only" ? null : (
                <div
                  key={`header-${columnIndex}`}
                  className={clsx(
                    "h-9 border-t relative flex items-center dark:border-white/5 dark:text-zinc-300 text-zinc-600",
                    columnIndex != 0 && "border-l"
                  )}
                  style={
                    column.type === "checkbox"
                      ? {
                          width: checkboxColWidths[columnIndex] || 60,
                          minWidth: checkboxColWidths[columnIndex] || 60,
                          maxWidth: checkboxColWidths[columnIndex] || 60,
                          flexShrink: 0,
                          flexGrow: 0,
                        }
                      : {
                          width: `calc(${columnPercents[columnIndex]}% - ${(
                            totalCheckboxWidth / nonCheckboxCount
                          ).toFixed(2)}px)`,
                          minWidth: 50,
                          flexShrink: 1,
                          flexGrow: 1,
                        }
                  }
                >
                  {columnIndex === 0 && (
                    <div className="flex items-center">
                      <Checkbox
                        className="mr-2 ml-1.5"
                        checked={
                          selectedRows.size === tableData.length &&
                          tableData.length > 0
                        }
                        indeterminate={
                          selectedRows.size > 0 &&
                          selectedRows.size < tableData.length
                        }
                        onCheckedChange={(checked) =>
                          handleHeaderCheckbox(!!checked)
                        }
                      />
                    </div>
                  )}
                  <div
                    className={clsx("text-sm", columnIndex > 0 ? "pl-2.5" : "")}
                  >
                    {column.name}
                    <span className="ml-1 text-xs text-zinc-400">
                      {column.type === "select" ? "(select)" : ""}
                    </span>
                  </div>
                  {/* Resize handle (skip if this or next column is checkbox) */}
                  {columnIndex < columns.length - 1 &&
                    column.type !== "checkbox" &&
                    columns[columnIndex + 1].type !== "checkbox" && (
                      <div
                        className="absolute right-0 top-0 w-1 h-full cursor-col-resize hover:bg-blue-500 z-30"
                        onMouseDown={handleMouseDown(columnIndex)}
                      />
                    )}
                </div>
              )
            )}
          </div>
          {/* Data rows */}
          {tableData.map((_, rowIndex) => (
            <div key={`row-${rowIndex}`} className="flex w-full">
              {columns.map((column, columnIndex) => {
                const isActive =
                  activeCell?.column === columnIndex &&
                  activeCell?.row === rowIndex;
                if (column.type === "read-only") return null;
                return (
                  <div
                    // Only set cell selection on click for non-checkbox columns and not for the row selection checkbox
                    onClick={
                      column.type === "select-with-quantities"
                        ? () => {
                            setActiveCell({
                              column: columnIndex,
                              row: rowIndex,
                            });
                            setOpenQuantitiesCell({
                              row: rowIndex,
                              col: columnIndex,
                            });
                          }
                        : column.type !== "checkbox"
                        ? () =>
                            setActiveCell({
                              column: columnIndex,
                              row: rowIndex,
                            })
                        : undefined
                    }
                    onMouseDown={(e) => {
                      // Prevent row selection checkbox from activating the cell
                      if (
                        columnIndex === 0 &&
                        (e.target as HTMLElement).closest(
                          ".row-select-checkbox"
                        )
                      ) {
                        return;
                      }
                      // Prevent value checkbox from activating the cell
                      if (
                        column.type === "checkbox" &&
                        (e.target as HTMLElement).closest(
                          ".cell-value-checkbox"
                        )
                      ) {
                        return;
                      }
                      // when the user clicks on a cell we set the selection start and end to the same cell
                      const cell = { row: rowIndex, column: columnIndex };
                      setSelectionStart(cell);
                      setSelectionEnd(cell);
                      setIsDragging(true);
                      setActiveCell(cell);
                    }}
                    // when the user hovers over a cell while dragging, we update the selection end
                    // to be the current cell. this grows the selection
                    onMouseEnter={() => {
                      if (isDragging) {
                        setSelectionEnd({
                          row: rowIndex,
                          column: columnIndex,
                        });
                      }
                    }}
                    // when the user stops dragging, we set isDragging to false
                    onMouseUp={() => {
                      setIsDragging(false);
                    }}
                    // only show the isSelected class if the cell is selected and isn't the active
                    key={`cell-${rowIndex}-${columnIndex}`}
                    className={clsx(
                      "h-9 relative",
                      isCellInSelection(rowIndex, columnIndex) &&
                        !(
                          activeCell?.column === columnIndex &&
                          activeCell?.row === rowIndex
                        )
                        ? getSelectionClasses(rowIndex, columnIndex)
                        : "border-t dark:border-white/5",
                      rowIndex === tableData.length - 1 ? "border-b" : "",
                      columnIndex != 0 && "border-l"
                    )}
                    style={
                      column.type === "checkbox"
                        ? {
                            width: checkboxColWidths[columnIndex] || 60,
                            minWidth: checkboxColWidths[columnIndex] || 60,
                            maxWidth: checkboxColWidths[columnIndex] || 60,
                            flexShrink: 0,
                            flexGrow: 0,
                          }
                        : {
                            width: `calc(${columnPercents[columnIndex]}% - ${(
                              totalCheckboxWidth / nonCheckboxCount
                            ).toFixed(2)}px)`,
                            minWidth: 50,
                            flexShrink: 1,
                            flexGrow: 1,
                          }
                    }
                    data-row={rowIndex}
                    data-column={columnIndex}
                  >
                    {/* Row selection checkbox: absolutely positioned, always clickable */}
                    {columnIndex === 0 && (
                      <div
                        className="flex items-center mr-1 absolute left-0 top-0 h-full z-30 pl-1"
                        style={{ pointerEvents: "auto" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          className="ml-0.5 row-select-checkbox"
                          checked={selectedRows.has(rowIndex)}
                          onCheckedChange={(checked) =>
                            handleRowCheckbox(rowIndex, !!checked)
                          }
                        />
                      </div>
                    )}
                    {isActive ? (
                      <div className="h-full">
                        <div
                          style={{
                            top: -1,
                            left: -1,
                            width: "calc(100% + 2px)",
                            height: "calc(100% + 2px)",
                          }}
                          className="z-10 rounded-sm absolute w-full top-0 h-full border border-blue-500/75 bg-blue-500/20"
                        ></div>
                        <div className="z-20 h-full relative">
                          {/* Render input based on column type */}
                          {column.type === "select" && column.options ? (
                            <Select
                              value={tableData[rowIndex][columnIndex]}
                              onValueChange={(val) =>
                                handleCellChange(rowIndex, columnIndex, val)
                              }
                              open={isShadcnSelectOpen(rowIndex, columnIndex)}
                              onOpenChange={(open) => {
                                if (!open) setOpenSelectCell(null);
                              }}
                            >
                              <SelectTrigger className="w-full h-7 text-sm px-1.5 h-full">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {column.options.map((opt) => (
                                  <SelectItem key={opt.id} value={opt.value}>
                                    {opt.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : column.type === "checkbox" ? (
                            <div
                              className="flex items-center justify-center h-full"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={!!tableData[rowIndex][columnIndex]}
                                onCheckedChange={(checked) =>
                                  handleCellChange(
                                    rowIndex,
                                    columnIndex,
                                    checked
                                  )
                                }
                                className="cell-value-checkbox"
                              />
                            </div>
                          ) : column.type === "select-with-quantities" ? (
                            <SelectWithQuantities
                              title={column.name}
                              options={column.options || []}
                              selectedOptions={(() => {
                                try {
                                  const val = tableData[rowIndex][columnIndex];
                                  if (!val) return new Map();
                                  const obj = JSON.parse(val);
                                  return new Map(
                                    Object.entries(obj).map(([k, v]) => [
                                      Number(k),
                                      v as number,
                                    ])
                                  );
                                } catch {
                                  return new Map();
                                }
                              })()}
                              onQuantityChange={(
                                id: number,
                                newQuantity: number
                              ) => {
                                // Get current value as object
                                let obj: Record<number, number> = {};
                                try {
                                  obj = tableData[rowIndex][columnIndex]
                                    ? JSON.parse(
                                        tableData[rowIndex][columnIndex]
                                      )
                                    : {};
                                } catch {
                                  obj = {};
                                }
                                if (newQuantity > 0) {
                                  obj[id] = newQuantity;
                                } else {
                                  delete obj[id];
                                }
                                handleCellChange(
                                  rowIndex,
                                  columnIndex,
                                  JSON.stringify(obj)
                                );
                              }}
                              dropdownOpen={isQuantitiesDropdownOpen(
                                rowIndex,
                                columnIndex
                              )}
                              setDropdownOpen={(open: boolean) => {
                                if (open) {
                                  setOpenQuantitiesCell({
                                    row: rowIndex,
                                    col: columnIndex,
                                  });
                                  setActiveCell({
                                    row: rowIndex,
                                    column: columnIndex,
                                  });
                                } else {
                                  setOpenQuantitiesCell(null);
                                }
                              }}
                            />
                          ) : (
                            <input
                              type="text"
                              className={`px-1.5 dark:text-white text-zinc-600 focus:ring-0 outline-none w-full h-full text-sm bg-transparent ${
                                columnIndex === 0 ? "pl-7" : ""
                              }`}
                              data-row={rowIndex}
                              data-column={columnIndex}
                              autoComplete="off"
                              data-1p-ignore
                              data-lpignore="true"
                              data-form-type="other"
                              value={tableData[rowIndex][columnIndex]}
                              onChange={(e) =>
                                handleCellChange(
                                  rowIndex,
                                  columnIndex,
                                  e.target.value
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`dark:text-zinc-400 text-zinc-600 text-sm truncate h-full flex items-center px-1.5 ${
                          columnIndex === 0 ? "pl-6" : ""
                        }`}
                      >
                        {/* Remove the old row checkbox here, it's now absolutely positioned above */}
                        {column.type === "checkbox" ? (
                          <div
                            className="flex items-center justify-center w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={!!tableData[rowIndex][columnIndex]}
                              onCheckedChange={(checked) =>
                                handleCellChange(rowIndex, columnIndex, checked)
                              }
                              className="z-50 cell-value-checkbox"
                            />
                          </div>
                        ) : column.type === "select-with-quantities" ? (
                          (() => {
                            // Parse the value and display as "2x Arena, 1x Production"
                            try {
                              const val = tableData[rowIndex][columnIndex];
                              if (!val) return null;
                              const obj = JSON.parse(val);
                              const entries = Object.entries(obj)
                                .filter(([_, qty]) => qty > 0)
                                .map(([id, qty]) => {
                                  const opt = column.options?.find(
                                    (o) => String(o.id) === id
                                  );
                                  return opt ? `${qty}x ${opt.value}` : null;
                                })
                                .filter(Boolean);
                              return entries.length > 0 ? (
                                <div className="pl-1">{entries.join(", ")}</div>
                              ) : null;
                            } catch {
                              return null;
                            }
                          })()
                        ) : (
                          tableData[rowIndex][columnIndex] != "" && (
                            <div className="ml-1">
                              {column.type === "select"
                                ? tableData[rowIndex][columnIndex]
                                : tableData[rowIndex][columnIndex]}
                            </div>
                          )
                        )}
                      </div>
                    )}
                    {/* Blue fill handle dot */}
                    {isCellInSelection(rowIndex, columnIndex) &&
                      isBottomRightOfSelection(rowIndex, columnIndex) && (
                        <div
                          onMouseDown={handleFillMouseDown(
                            rowIndex,
                            columnIndex
                          )}
                          className="absolute z-40"
                          style={{
                            right: -4,
                            bottom: -4,
                            width: 10,
                            height: 10,
                            cursor: "crosshair",
                            background: "#2563eb",
                            borderRadius: "50%",
                            border: "2px solid white",
                            boxShadow: "0 0 2px #2563eb",
                          }}
                        />
                      )}
                    {/* Fill preview highlight */}
                    {isFillDragging &&
                      fillTarget &&
                      (() => {
                        const bounds = getSelectionBounds();
                        if (!bounds) return null;
                        // Show preview for the rectangle from selection to fillTarget, excluding original selection
                        const previewStartRow = Math.min(
                          bounds.startRow,
                          fillTarget.row
                        );
                        const previewEndRow = Math.max(
                          bounds.endRow,
                          fillTarget.row
                        );
                        const previewStartCol = Math.min(
                          bounds.startCol,
                          fillTarget.column
                        );
                        const previewEndCol = Math.max(
                          bounds.endCol,
                          fillTarget.column
                        );
                        if (
                          (fillTarget.row !== bounds.endRow ||
                            fillTarget.column !== bounds.endCol) &&
                          rowIndex >= previewStartRow &&
                          rowIndex <= previewEndRow &&
                          columnIndex >= previewStartCol &&
                          columnIndex <= previewEndCol &&
                          (rowIndex < bounds.startRow ||
                            rowIndex > bounds.endRow ||
                            columnIndex < bounds.startCol ||
                            columnIndex > bounds.endCol)
                        ) {
                          return (
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                background: "rgba(37,99,235,0.15)",
                                border: "1.5px dashed rgba(37,99,235,0.7)",
                                zIndex: 30,
                              }}
                            />
                          );
                        }
                        return null;
                      })()}
                  </div>
                );
              })}
            </div>
          ))}
          {/* Add row controls */}
          <div className="flex items-center mt-3 space-x-3">
            <button
              onClick={handleAddRow}
              className="flex items-center dark:text-white dark:ring-white/10 inline-flex justify-center gap-x-1.5 rounded-md dark:bg-zinc-800 bg-white px-3 py-[0.3rem] text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:hover:bg-zinc-800 hover:bg-gray-100"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add row
            </button>

            <div className="flex items-center space-x-1 border-l pl-2">
              <input
                type="number"
                min={1}
                max={100}
                value={addRowsCount}
                onChange={(e) => setAddRowsCount(Number(e.target.value))}
                className="w-14 px-2 py-1 border rounded-md text-sm mr-1 dark:bg-zinc-900 dark:border-white/10 border-zinc-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                style={{ minWidth: 0 }}
              />
              <button
                onClick={handleAddRows}
                className="flex items-center dark:text-white dark:ring-white/10 inline-flex justify-center gap-x-1.5 rounded-md dark:bg-zinc-800 bg-white px-3 py-[0.3rem] text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:hover:bg-zinc-800 hover:bg-gray-100"
              >
                <ListPlus className="w-4 h-4 mr-1" />
                Add {addRowsCount > 1 ? addRowsCount : 1} rows
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Helper to parse pasted value for checkbox columns
function parseCheckboxValue(val: string): boolean {
  const normalized = val.trim().toLowerCase();
  if (normalized === "yes" || normalized === "true" || normalized === "1") {
    return true;
  }
  if (normalized === "no" || normalized === "false" || normalized === "0") {
    return false;
  }
  // fallback: treat empty as false, otherwise false
  return false;
}

// Helper to parse pasted value for select-with-quantities columns
function parseSelectWithQuantitiesValue(
  val: string,
  options: DropdownOption[]
): string {
  // Accept formats like: "x1 Arena, x2 Production" or "1x Arena, 2x Production"
  // or "Arena x1, Production x2"
  // We'll support: [optional x][number][optional x][space][name]
  // Split by comma
  const result: Record<number, number> = {};
  if (!val.trim()) return JSON.stringify(result);

  const parts = val
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  for (const part of parts) {
    // Try to match "x1 Arena" or "1x Arena" or "Arena x1"
    let match =
      part.match(/^x?(\d+)\s+(.+)$/i) || // x1 Arena
      part.match(/^(\d+)x\s+(.+)$/i) || // 1x Arena
      part.match(/^(.+)\s+x(\d+)$/i); // Arena x1

    if (match) {
      let qty: number, name: string;
      if (match[1] && match[2]) {
        qty = parseInt(match[1], 10);
        name = match[2].trim();
      } else if (match[2] && match[1]) {
        qty = parseInt(match[2], 10);
        name = match[1].trim();
      } else {
        continue;
      }
      // Find option by name (case-insensitive)
      const opt = options.find(
        (o) => o.value.trim().toLowerCase() === name.toLowerCase()
      );
      if (opt && qty > 0) {
        result[Number(opt.id)] = qty;
      }
    } else {
      // Try to match just the name (default to 1)
      const name = part.trim();
      const opt = options.find(
        (o) => o.value.trim().toLowerCase() === name.toLowerCase()
      );
      if (opt) {
        result[Number(opt.id)] = 1;
      }
    }
  }
  return JSON.stringify(result);
}
